import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import { transformRoute, useTranslate } from "helpers";
import { ContactRepresentativeCardProps, ContactRepresentativeRef } from "./types";
import { ContactRelationRow } from "services/api/contacts/types";
import {
  getContactRelationsAPI,
  deleteContactRelationAPI,
} from "services/api/contacts/api";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";
import ContactRepresentativeSearchResults from "./components/ContactRepresentativeSearchResults/ContactRepresentativeSearchResults";
import ContactRepresentativeEditCard from "../ContactRepresentativeEditCard/ContactRepresentativeEditCard";

const ContactRepresentativeCard = forwardRef<ContactRepresentativeRef, ContactRepresentativeCardProps>(({ contactDetail }, ref) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [relations, setRelations] = useState<ContactRelationRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [showAddNewModal, setShowAddNewModal] = useState(false);

  useImperativeHandle(ref, () => ({
    openAddNew: () => setShowAddNewModal(true),
  }));

  const fetchRelations = useCallback(async () => {
    if (!contactDetail.id) return;
    setIsLoading(true);
    try {
      const result = await getContactRelationsAPI(contactDetail.id);
      setRelations(result);
    } catch {
      setRelations([]);
    }
    setIsLoading(false);
  }, [contactDetail.id]);

  useEffect(() => {
    if (contactDetail.id) fetchRelations();
  }, [contactDetail.id]);

  const handlePersonClick = (personContactId: string) => {
    navigate(transformRoute(ContactRoutes.ContactDetailPageRouter, { id: personContactId }));
  };

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();
      setDeleteId(id);
      setOpenDialog(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!contactDetail.id || !deleteId) return;
    await deleteContactRelationAPI(contactDetail.id, deleteId);
    setOpenDialog(false);
    setDeleteId(null);
    fetchRelations();
  }, [contactDetail.id, deleteId]);

  const handleDeleteClose = useCallback(() => {
    setDeleteId(null);
    setOpenDialog(false);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowAddNewModal(false);
    fetchRelations();
  }, [fetchRelations]);

  if (contactDetail.contactType?.id !== "C") return null;

  return (
    <>
      <div className="space-y-3 mt-1">
        <div className="m-[-1px]">
          {!isLoading && relations.length === 0 ? (
            <NoResults message="Nema povezanih osoba" />
          ) : (
            <ContactRepresentativeSearchResults
              data={relations}
              isLoading={isLoading}
              onPersonClick={handlePersonClick}
              onDeleteClick={handleDeleteClick}
              onRowClick={(id) => {
                const rel = relations.find(r => r.id === id);
                if (rel?.personContact?.id) handlePersonClick(rel.personContact.id);
              }}
            />
          )}
        </div>
      </div>

      <ContactRepresentativeEditCard
        contactId={contactDetail.id ?? ""}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title="Uklanjanje predstavnika"
        message="Jeste li sigurni da želite ukloniti ovog predstavnika?"
        confirmText="Ukloni"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteClose}
      />
    </>
  );
});

export default memo(ContactRepresentativeCard);
